import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  LuBookOpen,
  LuDownload,
  LuRefreshCw,
  LuSparkles,
  LuCircleCheck,
  LuChevronDown,
  LuExternalLink,
  LuLoader,
} from "react-icons/lu";
import { Btn, Badge, Note, Skel } from "../components/Ui";

/* Study material for one roadmap step. The pack is generated once and cached
   server-side (one row per step) — material that changes every time you open it
   is not studyable. Read it here, or take the PDF away and study offline.

   Self-contained on purpose: the parent renders it keyed by step_id, so moving
   to another step remounts and resets all of this state. */

const STUDY_API = "https://us-east1-foursssolutions.cloudfunctions.net/career_study_pack_v2";

function downloadBase64(base64, mime, filename) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function callStudy(body) {
  const res = await fetch(STUDY_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}

/* Rendering has to match the PDF, or the same pack reads differently in the two
   places. Two things matter: paragraph breaks (one <p> each, otherwise it is a
   wall of text) and ``` fenced code, which must keep its indentation — a study
   pack about code is useless if the Python is flattened. */
function inlineMarkup(s, keyBase) {
  // `code` and **bold**, the only markdown the model emits inline
  const parts = String(s).split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (/^`[^`]+`$/.test(p)) return <code key={`${keyBase}-${i}`} className="sp-code-inline">{p.slice(1, -1)}</code>;
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>;
    return p;
  });
}

function Prose({ text }) {
  const raw = String(text || "");
  const segments = raw.split(/(```[a-zA-Z0-9_+-]*\n[\s\S]*?```)/g);
  const out = [];
  segments.forEach((seg, si) => {
    if (!seg || !seg.trim()) return;
    if (seg.trimStart().startsWith("```")) {
      const code = seg
        .trim()
        .replace(/^```[a-zA-Z0-9_+-]*\n?/, "")
        .replace(/```$/, "")
        .replace(/\n+$/, "");
      out.push(<pre key={`c-${si}`} className="sp-code"><code>{code}</code></pre>);
      return;
    }
    seg.split(/\n\s*\n|\n/).forEach((chunk, ci) => {
      const t = chunk.trim();
      if (t) out.push(<p key={`p-${si}-${ci}`} className="t-sm sp-p">{inlineMarkup(t, `${si}-${ci}`)}</p>);
    });
  });
  return <>{out}</>;
}

function Section({ title, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`sp-sec${open ? " open" : ""}`}>
      <button type="button" className="sp-sec-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="sp-sec-title">{title}</span>
        {typeof count === "number" ? <Badge tone="plain">{count}</Badge> : null}
        <LuChevronDown size={15} className="sp-caret" aria-hidden="true" />
      </button>
      {open ? <div className="sp-sec-body">{children}</div> : null}
    </div>
  );
}

/* A self-check, labelled as one. You read the question, answer it in your head,
   reveal the model answer, then grade your own recall. It is self-reported by
   nature — the copy says so rather than dressing it up as an objective test. */
function SelfCheck({ items, onDone, busy }) {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [marks, setMarks] = useState([]);
  /* The last answer submits but leaves this component mounted until the parent
     unmounts it, so without a latch the grading buttons stay live and fire
     onDone again — extra API calls and a score computed over too many marks. */
  const [submitted, setSubmitted] = useState(false);

  const item = items[idx];
  const last = idx >= items.length - 1;

  const mark = (value) => {
    if (submitted) return;
    const next = [...marks, value];
    setMarks(next);
    setRevealed(false);
    if (last) {
      setSubmitted(true);
      const score = Math.round((next.reduce((a, b) => a + b, 0) / items.length) * 100);
      onDone(score);
    } else {
      setIdx(idx + 1);
    }
  };

  return (
    <div className="sp-check">
      <div className="sp-check-top">
        <span className="t-label">Question {idx + 1} of {items.length}</span>
        <div className="sp-bar" aria-hidden="true">
          <span style={{ width: `${(idx / items.length) * 100}%` }} />
        </div>
      </div>
      <p className="t-sm sp-q">{item.question}</p>
      {revealed ? (
        <>
          <div className="sp-answer">
            <div className="t-label">Model answer</div>
            <Prose text={item.answer} />
          </div>
          <div className="t-label">How did you do?</div>
          <div className="sp-marks">
            <button type="button" className="cj-chip sm" onClick={() => mark(1)} disabled={busy || submitted}>Got it</button>
            <button type="button" className="cj-chip sm" onClick={() => mark(0.5)} disabled={busy || submitted}>Partly</button>
            <button type="button" className="cj-chip sm" onClick={() => mark(0)} disabled={busy || submitted}>Missed it</button>
          </div>
        </>
      ) : (
        <Btn className="btn-pill" variant="soft" onClick={() => setRevealed(true)}>
          Show model answer
        </Btn>
      )}
    </div>
  );
}

export default function CareerStudyPack({ email, step, onStepCompleted }) {
  const [state, setState] = useState("loading"); // loading | none | ready | failed
  const [pack, setPack] = useState(null);
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);
  const alive = useRef(true);

  /* Set on the way in as well as cleared on the way out. StrictMode mounts,
     unmounts and remounts in development, so a ref that is only ever cleared
     stays false after the remount and silently swallows every later setState. */
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await callStudy({ action: "get", email, step_id: step.step_id });
        if (cancelled) return;
        if (data.status === "success" && data.pack) {
          setPack(data.pack);
          setState(data.pack.status === "ready" ? "ready" : "failed");
          if (data.pack.status === "ready") {
            callStudy({ action: "mark_viewed", pack_id: data.pack.pack_id }).catch(() => {});
          }
        } else {
          setState("none");
        }
      } catch (err) {
        console.error("Load study pack failed:", err);
        if (!cancelled) setState("none");
      }
    })();
    return () => { cancelled = true; };
  }, [email, step.step_id]);

  const build = useCallback(async (force) => {
    setBuilding(true);
    setResult(null);
    try {
      const data = await callStudy({
        action: force ? "regenerate" : "generate",
        email,
        step_id: step.step_id,
      });
      if (data.status !== "success" || !data.pack) throw new Error(data.message || "generate failed");
      if (!alive.current) return;
      setPack(data.pack);
      setState("ready");
      toast.success(force ? "Study material rewritten" : "Study material ready");
    } catch (err) {
      console.error("Generate study pack failed:", err);
      toast.error("Couldn't build the study material. Please try again.");
      if (alive.current) setState((s) => (s === "ready" ? "ready" : "none"));
    } finally {
      if (alive.current) setBuilding(false);
    }
  }, [email, step.step_id]);

  const download = useCallback(async () => {
    if (!pack) return;
    setDownloading(true);
    try {
      const data = await callStudy({ action: "pdf", pack_id: pack.pack_id });
      if (data.status !== "success" || !data.base64) throw new Error(data.message || "pdf failed");
      downloadBase64(data.base64, data.content_type || "application/pdf", data.filename || "study-pack.pdf");
    } catch (err) {
      console.error("Study pack PDF failed:", err);
      toast.error("Couldn't prepare the PDF. Please try again.");
    } finally {
      if (alive.current) setDownloading(false);
    }
  }, [pack]);

  const submitScore = useCallback(async (score) => {
    setScoring(true);
    try {
      const data = await callStudy({ action: "record_practice", pack_id: pack.pack_id, score });
      if (data.status !== "success") throw new Error(data.message || "score failed");
      if (!alive.current) return;
      setResult({ score, completed: Boolean(data.step_completed) });
      setChecking(false);
      if (data.step_completed && onStepCompleted) {
        onStepCompleted({ progress_pct: data.progress_pct, readiness_pct: data.readiness_pct });
      }
    } catch (err) {
      console.error("Record practice failed:", err);
      toast.error("Couldn't save your self-check.");
    } finally {
      if (alive.current) setScoring(false);
    }
  }, [pack, onStepCompleted]);

  if (state === "loading") {
    return (
      <div className="cj-detail-block">
        <div className="t-label"><LuBookOpen size={13} /> Study material</div>
        <Skel h={54} r={10} />
      </div>
    );
  }

  if (state !== "ready") {
    return (
      <div className="cj-detail-block">
        <div className="t-label"><LuBookOpen size={13} /> Study material</div>
        <p className="t-sm">
          Build a study pack for this step: a primer written for your level and target role,
          worked examples, practice questions with model answers, likely interview questions,
          and the sources it draws on. Read it here or take the PDF with you.
        </p>
        {state === "failed" ? (
          <Note tone="amber">The last attempt didn't finish. You can try again.</Note>
        ) : null}
        <div>
          <Btn className="btn-pill btn-mint" onClick={() => build(false)} loading={building} disabled={building}>
            {building ? "Writing your material…" : <><LuSparkles size={15} /> Create study material</>}
          </Btn>
        </div>
        {building ? <p className="t-xs">This usually takes 20–30 seconds.</p> : null}
      </div>
    );
  }

  const c = pack.content || {};
  const examples = Array.isArray(c.worked_examples) ? c.worked_examples : [];
  const practice = Array.isArray(c.practice) ? c.practice : [];
  const questions = Array.isArray(c.interview_questions) ? c.interview_questions : [];
  const checklist = Array.isArray(c.checklist) ? c.checklist : [];
  const sources = Array.isArray(c.sources) ? c.sources : [];

  return (
    <div className="cj-detail-block sp-wrap">
      <div className="sp-head">
        <div className="t-label"><LuBookOpen size={13} /> Study material</div>
        <button type="button" className="sp-regen" onClick={() => build(true)} disabled={building}
          title="Write a fresh version of this pack">
          {building ? <LuLoader size={12} className="cj-spin" /> : <LuRefreshCw size={12} />} Rewrite
        </button>
      </div>

      {c.summary ? <p className="t-sm">{c.summary}</p> : null}

      <div className="sp-actions">
        <Btn className="btn-pill btn-mint" onClick={download} loading={downloading} disabled={downloading}>
          <LuDownload size={15} /> Download PDF
        </Btn>
        {practice.length && !checking && !result ? (
          <Btn className="btn-pill" variant="soft" onClick={() => setChecking(true)}>
            Self-check ({practice.length})
          </Btn>
        ) : null}
      </div>

      {result ? (
        <Note tone={result.completed ? "accent" : "info"}>
          {result.completed
            ? `You scored ${result.score}% on your self-check — this step is marked done.`
            : `You scored ${result.score}% on your self-check. Worth another pass before you mark this step done.`}
        </Note>
      ) : null}

      {checking ? (
        <SelfCheck items={practice} onDone={submitScore} busy={scoring} />
      ) : null}

      {c.primer ? (
        <Section title="Primer" defaultOpen>
          <Prose text={c.primer} />
        </Section>
      ) : null}

      {examples.length ? (
        <Section title="Worked examples" count={examples.length}>
          {examples.map((ex, i) => (
            <div key={i} className="sp-item">
              <div className="sp-item-title">{ex.title}</div>
              <Prose text={ex.walkthrough} />
            </div>
          ))}
        </Section>
      ) : null}

      {practice.length ? (
        <Section title="Practice" count={practice.length}>
          {practice.map((p, i) => (
            <div key={i} className="sp-item">
              <div className="sp-item-title">{p.question}</div>
              <Prose text={p.answer} />
            </div>
          ))}
        </Section>
      ) : null}

      {questions.length ? (
        <Section title="Interview questions" count={questions.length}>
          {questions.map((q, i) => (
            <div key={i} className="sp-item">
              <div className="sp-item-title">{q.question}</div>
              <Prose text={q.what_good_looks_like} />
            </div>
          ))}
        </Section>
      ) : null}

      {checklist.length ? (
        <Section title="You are done when" count={checklist.length}>
          <ul className="sp-check-list">
            {checklist.map((line, i) => (
              <li key={i}><LuCircleCheck size={13} aria-hidden="true" /> <span>{line}</span></li>
            ))}
          </ul>
        </Section>
      ) : null}

      {sources.length ? (
        <Section title="Sources" count={sources.length}>
          <ul className="cj-res">
            {sources.map((s, i) => {
              const url = typeof s.where === "string" && /^https?:\/\//i.test(s.where) ? s.where : "";
              return (
                <li key={i}>
                  {url ? (
                    <a href={url} target="_blank" rel="noreferrer" className="t-accent">
                      {s.title} <LuExternalLink size={12} />
                    </a>
                  ) : (
                    <>{s.title}{s.where ? ` — ${s.where}` : ""}</>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      <p className="t-xs sp-disclaimer">
        This material is AI-generated from your roadmap step and may contain errors. Check it
        against the sources listed above before relying on it.
      </p>
    </div>
  );
}
